import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSessionStore } from '@features/auth';
import { getDateRangeFromPreset, useDashboardStore } from '@features/dashboard';
import useDashboardApi from './useDashboardApi.ts';
import {
  type IntegrationJob,
  type OrganizationInfo,
  type PaginatedWithMetricsResponse
} from '@features/dashboard/types/dashboard.types.ts';
import { logError } from '@lib/logger/logError.ts';
import { type ApiResponse } from '@types';

export interface UseDashboardIntegrationsViewModelReturn {
  // Data
  selectedHubspotJobs: IntegrationJob[] | null;
  selectedOutreachJobs: IntegrationJob[] | null;
  selectedSalesforceJobs: IntegrationJob[] | null;

  // Pagination
  currentPage: number;
  totalStatusSuccess: number;
  totalStatusCreated: number;
  totalStatusFailed: number;
  totalCountJobs: number;
  totalCountHs: number;
  totalCountOr: number;
  totalCountSf: number;
  rowsPerPage: number;
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;

  // Loading
  isLoadingHubspotJobs: boolean;
  isLoadingOutreachJobs: boolean;
  isLoadingSalesforceJobs: boolean;

  // User Context
  currentUserId: string;
  organizations: OrganizationInfo[];
  isTendrilSelected: boolean;

  // Actions
  openHubspotJobs: () => Promise<void>;
  openOutreachJobs: () => Promise<void>;
  openSalesforceJobs: () => Promise<void>;
  retryHubspotJob: (jobId: string, includeSkipped?: boolean) => Promise<void>;
  retryOutreachJob: (jobId: string) => Promise<void>;
  retrySalesforceJob: (jobId: string) => Promise<void>;
}

export default function useDashboardIntegrationsViewModel ():UseDashboardIntegrationsViewModelReturn {
  const dashboardApi = useDashboardApi();
  const { currentUser } = useSessionStore();
  const currentUserId = currentUser?.userId ?? '';
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;
  // Organizations state (for filter dropdown)
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  
  const {
    filters,
    currentPage,
    rowsPerPage,
    setCurrentPage: setStorePage,
    setRowsPerPage: setStoreRowsPerPage,
  } = useDashboardStore();

  const organizationId = isSuperAdmin ? (filters.orgIds?.[0] ?? '') : (currentUser?.organizationId ?? '');

  const [totalCountHs, setTotalCountHs] = useState(0);
  const [totalCountOr, setTotalCountOr] = useState(0);
  const [totalCountSf, setTotalCountSf] = useState(0);
  const [statusSuccessHs, setStatusSuccessHs] = useState(0);
  const [statusSuccessOr, setStatusSuccessOr] = useState(0);
  const [statusSuccessSf, setStatusSuccessSf] = useState(0);
  const [statusCreatedHs, setStatusCreatedHs] = useState(0);
  const [statusCreatedOr, setStatusCreatedOr] = useState(0);
  const [statusCreatedSf, setStatusCreatedSf] = useState(0);
  const [statusFailedHs, setStatusFailedHs] = useState(0);
  const [statusFailedOr, setStatusFailedOr] = useState(0);
  const [statusFailedSf, setStatusFailedSf] = useState(0);
  const [isLoadingHubspotJobs, setIsLoadingHubspotJobs] = useState(false);
  const [selectedHubspotJobs, setSelectedHubspotJobs] = useState<IntegrationJob[] | null>([]);
  const [isLoadingOutreachJobs, setIsLoadingOutreachJobs] = useState(false);
  const [selectedOutreachJobs, setSelectedOutreachJobs] = useState<IntegrationJob[] | null>([]);
  const [isLoadingSalesforceJobs, setIsLoadingSalesforceJobs] = useState(false);
  const [selectedSalesforceJobs, setSelectedSalesforceJobs] = useState<IntegrationJob[] | null>([]);

  // Cache for organization names (loaded once, stored as ref to avoid re-render cascades)
  const orgNameMapRef = useRef<Map<string, string>>(new Map());
  const orgsLoadedRef = useRef(false);

  // Load organizations once on mount, then trigger initial session fetch
  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    const loadOrganizations = async (): Promise<void> => {
      const orgsResponse = await dashboardApi.getOrganizationsAsync();
      if (orgsResponse.success && orgsResponse.data !== undefined) {
        setOrganizations(orgsResponse.data);
        const nameMap = new Map<string, string>();
        orgsResponse.data.forEach((org) => {
          nameMap.set(org.id, org.name);
        });
        orgNameMapRef.current = nameMap;
        orgsLoadedRef.current = true;

        // Default to "Tendril" org if no org is selected
        const { filters: currentFilters, setFilters: updateFilters } = useDashboardStore.getState();
        if ((currentFilters.orgIds?.length ?? 0) === 0) {
          const tendrilOrg = orgsResponse.data.find(
            (org) => org.name.toLowerCase().includes('tendril'),
          );
          if (tendrilOrg !== undefined) {
            updateFilters({ orgIds: [tendrilOrg.id] });
          }
        }
      }
    };
    void loadOrganizations();
  }, [isSuperAdmin]);

  const isTendrilSelected = useMemo(() => {
    const selectedOrg = organizations.find((org) => org.id === filters.orgIds?.[0]);
    return selectedOrg?.name.toLowerCase().includes('tendril') ?? false;
  }, [filters.orgIds, organizations]);

  const effectiveFilters = useMemo(() => {
    const result = { ...filters };

    if (
      result.dateRange?.from == null &&
        result.dateRange?.preset !== 'custom'
    ) {
      const dateRange = getDateRangeFromPreset(
        result.dateRange?.preset ?? 'thisMonth'
      );

      result.dateRange = {
        ...result.dateRange,
        from: dateRange.from,
        to: dateRange.to,
      };
    }

    return result;
  }, [filters]);

  const openHubspotJobs = useCallback(async () => {
    if (currentUserId === '') {
      return;
    }
    setIsLoadingHubspotJobs(true);

    try {
      const response: ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>> = await dashboardApi.getHubspotJobs(organizationId, effectiveFilters, String(currentPage), String(rowsPerPage));

      if (!response.success) {
        throw new Error(response?.error?.message ?? 'Unknown error. dashboardIntegationsGetHubspotJobs');
      }

      const hubspotJobs = response.data?.data as unknown as IntegrationJob[];
      setSelectedHubspotJobs(hubspotJobs);
      setTotalCountHs(response.data?.totalCount ?? 0);
      setStatusSuccessHs(response.data?.totalStatusSuccess ?? 0);
      setStatusCreatedHs(response.data?.totalStatusCreated ?? 0);
      setStatusFailedHs(response.data?.totalStatusError ?? 0);
      setIsLoadingHubspotJobs(false);
    } catch (error) {
      setIsLoadingHubspotJobs(false);
      void logError('Failed to load Hubspot jobs.', 'DashboardIntegrationsViewModel.openHubspotJobs', { error, organizationId }, false);
      throw error;
    }
  }, [currentUserId, organizationId, currentPage, rowsPerPage, dashboardApi, filters]);

  const openOutreachJobs = useCallback(async () => {
    if (currentUserId === '') {
      return;
    }
    setIsLoadingOutreachJobs(true);

    try {
      const response: ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>> = await dashboardApi.getOutreachJobs(organizationId, effectiveFilters, String(currentPage), String(rowsPerPage));

      if (!response.success) {
        throw new Error(response?.error?.message ?? 'Unknown error. dashboardIntegationsGetOutreachJobs');
      }

      const outreachJobs = response.data?.data as unknown as IntegrationJob[];
      setSelectedOutreachJobs(outreachJobs);
      setTotalCountOr(response.data?.totalCount ?? 0);
      setStatusSuccessOr(response.data?.totalStatusSuccess ?? 0);
      setStatusCreatedOr(response.data?.totalStatusCreated ?? 0);
      setStatusFailedOr(response.data?.totalStatusError ?? 0);
      setIsLoadingOutreachJobs(false);
    } catch (error) {
      setIsLoadingOutreachJobs(false);
      void logError('Failed to load Outreach jobs.', 'DashboardIntegrationsViewModel.openOutreachJobs', { error, organizationId }, false);
      throw error;
    }
  }, [currentUserId, organizationId, currentPage, rowsPerPage, dashboardApi, filters]);

  const openSalesforceJobs = useCallback(async () => {
    if (currentUserId === '') {
      return;
    }
    setIsLoadingSalesforceJobs(true);
    
    try {
      const response: ApiResponse<PaginatedWithMetricsResponse<IntegrationJob>> = await dashboardApi.getSalesforceJobs(organizationId, effectiveFilters, String(currentPage), String(rowsPerPage));
      
      if (!response.success) {
        throw new Error(response?.error?.message ?? 'Unknown error. dashboardIntegationsGetSalesforceJobs');
      }
      
      const salesforceJobs = response.data?.data as unknown as IntegrationJob[];
      setSelectedSalesforceJobs(salesforceJobs);
      setTotalCountSf(response.data?.totalCount ?? 0);
      setStatusSuccessSf(response.data?.totalStatusSuccess ?? 0);
      setStatusCreatedSf(response.data?.totalStatusCreated ?? 0);
      setStatusFailedSf(response.data?.totalStatusError ?? 0);
      setIsLoadingSalesforceJobs(false);
    } catch (error) {
      setIsLoadingSalesforceJobs(false);
      void logError('Failed to load Salesforce jobs.', 'DashboardIntegrationsViewModel.openSalesforceJobs', { error, organizationId }, false);
      throw error;
    }
  }, [currentUserId, organizationId, currentPage, rowsPerPage, dashboardApi, filters]);

  const retryHubspotJob = useCallback(async (
    jobId: string,
    includeSkipped = false,
  ) => {
    try {
      const response = await dashboardApi.retryHubspotJob(jobId, includeSkipped);

      if (!response.success) {
        throw new Error(response?.error?.message ?? 'Unknown error. dashboardIntegationsRetryHubspotJob');
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      await openHubspotJobs();
    } catch (error) {
      void logError('Failed to retry Hubspot job.', 'DashboardIntegrationsViewModel.retryHubspotJob', { error, jobId, includeSkipped }, false);
      throw error;
    }
  }, [dashboardApi, openHubspotJobs]);

  const retryOutreachJob = useCallback(async (
    jobId: string
  ) => {
    try {
      const response = await dashboardApi.retryOutreachJob(jobId);

      if (!response.success) {
        throw new Error(response?.error?.message ?? 'Unknown error. dashboardIntegationsRetryOutreachJob');
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      await openOutreachJobs();
    } catch (error) {
      void logError('Failed to retry Outreach job.', 'DashboardIntegrationsViewModel.retryOutreachJob', { error, jobId }, false);
      throw error;
    }
  }, [dashboardApi, openOutreachJobs]);

  const retrySalesforceJob = useCallback(async (
    jobId: string
  ) => {
    try {
      const response = await dashboardApi.retrySalesforceJob(jobId);

      if (!response.success) {
        throw new Error(response?.error?.message ?? 'Unknown error. dashboardIntegationsRetrySalesforceJob');
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
      await openSalesforceJobs();
    } catch (error) {
      void logError('Failed to retry Salesforce job.', 'DashboardIntegrationsViewModel.retrySalesforceJob', { error, jobId }, false);
      throw error;
    }
  }, [dashboardApi]);

  const setCurrentPage = useCallback((page: number) => {
    setStorePage(page);
  }, [setStorePage]);

  const setRowsPerPage = useCallback((rows: number) => {
    setStoreRowsPerPage(rows);
  }, [setStoreRowsPerPage]);

  const totalCountJobs = totalCountHs + totalCountOr + totalCountSf;
  const totalStatusSuccess = statusSuccessHs + statusSuccessOr + statusSuccessSf;
  const totalStatusCreated = statusCreatedHs + statusCreatedOr + statusCreatedSf;
  const totalStatusFailed = statusFailedHs + statusFailedOr + statusFailedSf;
  
  return {
    // Data
    selectedOutreachJobs,
    selectedHubspotJobs,
    selectedSalesforceJobs,
    
    // Pagination
    currentPage,
    totalStatusSuccess,
    totalStatusCreated,
    totalStatusFailed,
    totalCountJobs,
    totalCountHs,
    totalCountOr,
    totalCountSf,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
    
    // Loading
    isLoadingHubspotJobs,
    isLoadingOutreachJobs,
    isLoadingSalesforceJobs,
    
    // User Context
    currentUserId,
    organizations,
    isTendrilSelected,

    // Actions
    openHubspotJobs,
    openOutreachJobs,
    openSalesforceJobs,
    retryHubspotJob,
    retryOutreachJob,
    retrySalesforceJob
  };
}